import { QuestionMarkIcon } from '@/components'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Text,
} from '@chakra-ui/react'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { Offer } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import { Controller, useFormContext, type Control, type FieldPathByValue, type FieldValues } from 'react-hook-form'


type AgreementsProps<T extends FieldValues> = {
  control: Control<T>
  agreementName?: FieldPathByValue<T, boolean | null | undefined>
  agreementOtherName?: FieldPathByValue<T, boolean | null | undefined>
  agreementWarningMessageName?: FieldPathByValue<T, string | null | undefined>
  isDisabled?: boolean
  offer?: Offer
  agreementTooltip?: React.ReactNode
}

export function Agreements<T extends FieldValues>({
  control,
  agreementName,
  agreementOtherName,
  agreementWarningMessageName,
  isDisabled = false,
  offer,
  agreementTooltip,
}: AgreementsProps<T>) {
  const { app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const methods = useFormContext()
  const {
    getValues,
    getFieldState,
    watch
  } = methods

  const { error: agreementError } = agreementName ? getFieldState(agreementName) : { error: undefined }
  const { error: agreementOtherError } = agreementOtherName ? getFieldState(agreementOtherName) : { error: undefined }

  const agreementWarning = agreementWarningMessageName ? getValues(agreementWarningMessageName) : undefined

  const titleAgreement =
        offer?.insurerId == Insurer.GeneraliCeskaPojistovna
          ? 'Souhlas s el. komunikací při jednání o uzavření pojistné smlouvy'
          : 'Souhlas s elektronickou komunikací'

  const tooltipAgreement = agreementTooltip
    ? agreementTooltip
    : (
      offer?.insurerId == Insurer.GeneraliCeskaPojistovna
        ? (
          <Box maxW="lg">
            <Text>
              Klient souhlasí a požaduje odesílání informací a dokumentů při jednání
              o uzavření PS elektronicky (e-mailem příp. SMS).
            </Text>
            <br />
            <Text>
              Při nesouhlasu budou dokumenty při jednání o uzavření PS zaslány v
              listinné podobě.
            </Text>
            <br />
            <Text>
              Dokumenty při jednání o uzavření PS jsou například Pojistná smlouva,
              Nabídka na uzavření smlouvy, jakákoliv Nabídka s rozsahem pojištění,
              Záznam z jednání, Informace o zprostředkovateli, Informace k produktu
              - IPID, PI, VPP apod.
            </Text>
          </Box>
        )
        : (
          <Box>
            <Text>
              Klient souhlasí a požaduje odesílání informací a dokumentů elektronicky (e-mailem příp. SMS).
            </Text>
          </Box>
        ))

  const tooltipAgreementOther = (
    <Box maxW="lg">
      <Text>
        Klient souhlasí a požaduje odesílání informací a dokumentů během trvání
        pojištění a při změně PS elektronicky (e-mailem příp. SMS).
      </Text>
      <br />
      <Text>
        Při nesouhlasu budou dokumenty během trvání pojištění a při změně PS
        zaslány v listinné podobě.
      </Text>
      <br />
      <Text>
        POZOR: nesouhlas ovlivňuje všechny klientovy PS (aktivní i budoucí). V
        případě nesouhlasu, nebudou klientovi odcházet e-maily ani SMS na žádnou
        z aktivních PS v NŽP i ŽP.
      </Text>
      <br />
      <Text>
        Dokumenty během trvání pojištění jsou například Upozornění na splatnost,
        Výroční dopis, Upozornění na chybějící platbu a při změně PS například
        Informace o pojišťovně, produktu, nebo zprostředkovateli, Záznam z
        jednání , Pojistnou smlouvu a další dokumenty.
      </Text>
    </Box>
  )

  return (
    <>
      {/* Agreement */}
      {agreementName &&
                <FormControl isInvalid={!!agreementError?.message}>
                  <Flex sx={{ gap: 2, alignItems: 'center' }}>
                    <Controller
                      control={control}
                      name={agreementName}
                      render={({ field }) => (
                        <Checkbox
                          isChecked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          onBlur={field.onBlur}
                          isDisabled={isDisabled}
                        >
                          {titleAgreement}
                        </Checkbox>
                      )}
                    />

                    {tooltipAgreement && (
                      <CustomTooltip
                        trigger="clickHover"
                        hasArrow
                        label={tooltipAgreement}
                        placement="top"
                      >
                        <QuestionMarkIcon />
                      </CustomTooltip>
                    )}
                  </Flex>

                  {agreementWarning && (
                    <Box mt={2}>
                      <Text variant="warning" fontSize="sm">
                        {agreementWarning}
                      </Text>
                    </Box>
                  )}

                  <FormErrorMessage>{agreementError?.message as any}</FormErrorMessage>
                </FormControl>
      }

      {/* Agreement Other (podmíněné vykreslení) */}
      {offer?.insurerId == Insurer.GeneraliCeskaPojistovna && agreementOtherName && (
        <FormControl isInvalid={!!agreementOtherError?.message} mt={4}>
          <Flex sx={{ gap: 8, alignItems: 'center' }}>
            <Controller
              control={control}
              name={agreementOtherName}
              render={({ field }) => (
                <Checkbox
                  isChecked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  isDisabled={isDisabled}
                >
                  Souhlas s el. komunikací během trvání pojištění
                </Checkbox>
              )}
            />
            <CustomTooltip
              trigger="clickHover"
              hasArrow
              label={tooltipAgreementOther}
              placement="top"
            >
              <QuestionMarkIcon />
            </CustomTooltip>
          </Flex>

          <FormErrorMessage>
            {agreementOtherError?.message as any}
          </FormErrorMessage>
        </FormControl>
      )}
    </>
  )
}
